import { ReactNode } from 'react';

import { FilterOp } from '@/features/backlog/types';
import { Popover, menuRow } from './Popover';
import { Check, ChevronDown } from './icons';
import { cn } from './cn';

/** The "Is / Is not" segmented toggle shown at the top of a facet popover (include vs exclude the
 *  selected values). Rendered only when an `onOpChange` is supplied. */
function OpToggle({ op, onOpChange }: { op: FilterOp; onOpChange: (op: FilterOp) => void }) {
  return (
    <div className="mb-1 flex items-center gap-1 border-b border-line px-1 pb-1.5 pt-0.5">
      {(['is', 'isNot'] as const).map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onOpChange(o)}
          aria-pressed={op === o}
          className={cn(
            'flex-1 rounded-md px-2 py-1 text-[12px] font-medium transition-colors',
            op === o ? 'bg-accent/15 text-fg' : 'text-fg-faint hover:bg-layer hover:text-fg-soft',
          )}
        >
          {o === 'is' ? 'Is' : 'Is not'}
        </button>
      ))}
    </div>
  );
}

export type FacetOption = {
  value: string;
  label: string;
  /** Leading visual (status swatch / member avatar) — the same marks the cards use. */
  lead?: ReactNode;
  count?: number;
};

/**
 * The multi-select option rows (leading swatch/avatar · label · count · check) + Clear, shared by
 * {@link FacetSelect}'s popover and the filter bar's overflow (⋮ more) menu so both read identically.
 */
export function FacetOptionsList({
  options,
  selected,
  onChange,
  op,
  onOpChange,
}: {
  options: FacetOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Current is/is-not operator + its setter. Both present → the "Is / Is not" toggle is shown on top. */
  op?: FilterOp;
  onOpChange?: (op: FilterOp) => void;
}) {
  const active = selected.length;
  const toggle = (v: string) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <>
      {onOpChange ? <OpToggle op={op ?? 'is'} onOpChange={onOpChange} /> : null}
      {options.map((o) => {
        const checked = selected.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            role="checkbox"
            aria-checked={checked}
            onClick={() => toggle(o.value)}
            className={cn(menuRow, checked ? 'text-fg' : 'text-fg-soft')}
          >
            {o.lead ?? null}
            <span className="flex-1 truncate">{o.label}</span>
            {o.count != null ? <span className="tnum text-[11px] text-fg-faint">{o.count}</span> : null}
            <span className="flex w-3.5 shrink-0 justify-center text-accent">{checked ? <Check /> : null}</span>
          </button>
        );
      })}
      {active > 0 ? (
        <button
          type="button"
          onClick={() => onChange([])}
          className="mt-1 flex min-h-[48px] w-full items-center rounded-lg border-t border-line px-2.5 text-left text-[12px] text-fg-faint transition-colors hover:text-fg-soft"
        >
          Clear
        </button>
      ) : null}
    </>
  );
}

/**
 * The filter-bar facet dropdown. Uses the same anchored {@link Popover} + option rows as
 * the backlog cards' status/assignee menus (leading swatch/avatar · label · check), but
 * multi-select: a trailing check marks every chosen value and a Clear row resets the facet.
 */
export function FacetSelect({
  label,
  options,
  selected,
  onChange,
  op,
  onOpChange,
}: {
  label: string;
  options: FacetOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  op?: FilterOp;
  onOpChange?: (op: FilterOp) => void;
}) {
  const active = selected.length;
  const isNot = (op ?? 'is') === 'isNot';

  return (
    <Popover
      trigger={({ open, toggle: openToggle }) => (
        <button
          type="button"
          onClick={openToggle}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            // Match the board / view switcher pills (rounded-full, 36px, filled, semibold) so the
            // whole header/toolbar row of dropdowns reads as one family.
            'inline-flex min-h-[36px] items-center gap-2 rounded-full px-3 text-[13px] font-semibold transition-colors',
            active > 0 ? 'bg-accent/15 text-fg' : 'bg-layer text-fg-strong hover:bg-layer-strong',
          )}
        >
          <span>{label}</span>
          {/* Show the "is not" operator on the pill when it's negating an active selection, so the
              exclusion is visible without opening the popover. */}
          {active > 0 && isNot ? (
            <span className="rounded bg-rose-500/15 px-1 py-px text-[10px] font-semibold uppercase tracking-wide text-rose-500">
              not
            </span>
          ) : null}
          {active > 0 ? (
            <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-accent/25 px-1 text-[11px] font-semibold text-accent">
              {active}
            </span>
          ) : null}
          <ChevronDown open={open} className="text-fg-faint" />
        </button>
      )}
    >
      {() => <FacetOptionsList options={options} selected={selected} onChange={onChange} op={op} onOpChange={onOpChange} />}
    </Popover>
  );
}

export default FacetSelect;
